import { MAX_TESTIMONIALS_PER_CATEGORY } from '../../../constants';
import { Testimonial, TestimonialCategories } from '../types/testimonial';

/**
* Sorts testimonials by homepage widget priority first (only when isHomepage is true), then by category priority, and finally by date.
* Returns an empty array if no categories are provided.
*
* @param testimonials - Array of testimonial objects to sort
* @param testimonialCategories - Object mapping category names to priority numbers (lower = higher priority)
* @param limit - Maximum number of testimonials to return (default: 9)
* @param isHomepage - Whether this is the homepage (default: false). When true, isHomepageWidget testimonials get priority
* @returns Sorted array of testimonials, limited to the specified count
*/
const sortTestimonialsByCategory = (
    testimonials: Testimonial[],
    testimonialCategories: TestimonialCategories | null | undefined,
    limit = MAX_TESTIMONIALS_PER_CATEGORY,
    isHomepage = false,
): Testimonial[] => {
    if (!testimonialCategories) {
        return [];
    }

    return testimonials
        .sort((a: Testimonial, b: Testimonial) => {
            // First priority: isHomepageWidget (only when on homepage)
            if (isHomepage && a.isHomepageWidget !== b.isHomepageWidget) {
                return a.isHomepageWidget ? -1 : 1;
            }

            // Second priority: category priority (lower number = higher priority)
            const categoryDiff = (testimonialCategories[a.category] ?? 99) - (testimonialCategories[b.category] ?? 99);
            if (categoryDiff !== 0) {
                return categoryDiff;
            }

            // Third priority: date (newer dates first)
            return new Date(b.date).getTime() - new Date(a.date).getTime();
        })
        .slice(0, limit);
};

export default sortTestimonialsByCategory;
